export type WatermarkOptions = {
  /** URL eines Overlay-Bildes (PNG mit Transparenz empfohlen). Wenn gesetzt, wird statt Text dieses Bild eingebrannt. */
  imageUrl?: string | null;
  /** Horizontale Position des Wasserzeichen-Mittelpunkts in % der Bildbreite (0-100). Default 50. */
  x?: number;
  /** Vertikale Position des Wasserzeichen-Mittelpunkts in % der Bildhöhe (0-100). Default 92. */
  y?: number;
  /** Breite des Wasserzeichens in % der Bildbreite (1-100). Default 25. */
  scale?: number;
  /** Deckkraft in % (0-100). Default 94. */
  opacity?: number;
};

/**
 * Brennt ein Wasserzeichen ins Bild — entweder ein Overlay-Bild oder als Fallback den Text „OM Selected".
 */
export async function watermarkImage(file: File, opts: WatermarkOptions = {}): Promise<File> {
  const dataUrl = await fileToDataUrl(file);
  const img = await loadImage(dataUrl);

  // DM Serif Display ist im Stylesheet eingebunden; sicherheitshalber laden.
  try {
    if (typeof document !== "undefined" && (document as any).fonts?.load) {
      await (document as any).fonts.load('400 64px "DM Serif Display"');
    }
  } catch {
    /* noop */
  }

  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas wird nicht unterstützt.");
  ctx.drawImage(img, 0, 0);

  const W = canvas.width;
  const H = canvas.height;
  const opacity = clamp(opts.opacity ?? 94, 0, 100) / 100;

  if (opts.imageUrl) {
    try {
      const overlay = await loadImageCors(opts.imageUrl);
      const scalePct = clamp(opts.scale ?? 25, 1, 100) / 100;
      const targetW = W * scalePct;
      const ratio = overlay.naturalWidth > 0 ? overlay.naturalHeight / overlay.naturalWidth : 1;
      const targetH = targetW * ratio;
      const cx = (clamp(opts.x ?? 50, 0, 100) / 100) * W;
      const cy = (clamp(opts.y ?? 92, 0, 100) / 100) * H;
      ctx.globalAlpha = opacity;
      ctx.drawImage(overlay, cx - targetW / 2, cy - targetH / 2, targetW, targetH);
      ctx.globalAlpha = 1;
    } catch (e) {
      console.warn("[watermark] image overlay failed, falling back to text", e);
      drawTextWatermark(ctx, W, H, opacity);
    }
  } else {
    drawTextWatermark(ctx, W, H, opacity);
  }

  const mime = file.type === "image/png" ? "image/png" : "image/jpeg";
  const quality = mime === "image/jpeg" ? 0.92 : undefined;
  const blob: Blob = await new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Bild konnte nicht erzeugt werden."))),
      mime,
      quality,
    ),
  );

  const ext = mime === "image/png" ? "png" : "jpg";
  const baseName = file.name.replace(/\.[^.]+$/, "") || "image";
  return new File([blob], `${baseName}-omselected.${ext}`, { type: mime });
}

function drawTextWatermark(ctx: CanvasRenderingContext2D, W: number, H: number, opacity: number) {
  const pad = Math.round(H * 0.035);
  const size = Math.round(H * 0.045);
  ctx.font = `400 ${size}px "DM Serif Display", "Fraunces", Georgia, serif`;
  ctx.textBaseline = "alphabetic";
  ctx.textAlign = "left";
  const text = "OM  Selected";
  const x = pad;
  const y = H - pad;
  ctx.fillStyle = `rgba(255,255,255,${opacity})`;
  ctx.fillText(text, x, y);
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(fr.error ?? new Error("Datei konnte nicht gelesen werden."));
    fr.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Bild konnte nicht geladen werden."));
    img.src = src;
  });
}

function loadImageCors(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Wasserzeichen-Bild konnte nicht geladen werden."));
    img.src = src;
  });
}